import { CategoryQuestions } from '../types';

export const motivationQuestions: CategoryQuestions = {
  title: 'Personalize Your Motivation Session',
  questions: {
    preferredStyle: {
      label: 'What type of motivation works best for you?',
      type: 'radio',
      options: ['High-energy encouragement', 'Calm steady guidance', 'Challenging push', 'Supportive coaching']
    },
    primaryGoal: {
      label: 'What are you seeking motivation for?',
      type: 'radio',
      options: ['Career and success', 'Personal growth', 'Health and wellness', 'Life challenges']
    },
    specificFocus: {
      label: 'Which goal or challenge needs a boost today?',
      type: 'textarea',
      placeholder: 'Optional - leave blank for general session'
    },
    triggers: {
      label: 'Why is this important to you right now?',
      type: 'textarea',
      placeholder: 'What\'s the deeper reason this matters to you? (Optional but helps create a more powerful session)'
    },
    specialOccasions: {
      label: 'Are you celebrating or recognizing anything special today?',
      type: 'textarea',
      placeholder: 'e.g., birthday, promotion, anniversary, achievement, milestone...'
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for you?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    }
  },
  // Practitioner perspective questions
  practitionerTitle: 'Personalize Motivation Session for Your Client',
  practitionerQuestions: {
    preferredStyle: {
      label: 'What type of motivation works best for your client?',
      type: 'radio',
      options: ['High-energy encouragement', 'Calm steady guidance', 'Challenging push', 'Supportive coaching']
    },
    primaryGoal: {
      label: 'What is your client seeking motivation for?',
      type: 'radio',
      options: ['Career and success', 'Personal growth', 'Health and wellness', 'Life challenges']
    },
    specificFocus: {
      label: 'Which goal or challenge does your client need a boost with today?',
      type: 'textarea',
      placeholder: 'Optional - leave blank for general session'
    },
    triggers: {
      label: 'Why is this important to your client right now?',
      type: 'textarea',
      placeholder: 'What\'s the deeper reason this matters to them? (Optional but helps create a more powerful session)'
    },
    specialOccasions: {
      label: 'Is your client celebrating or recognizing anything special today?',
      type: 'textarea',
      placeholder: 'e.g., birthday, promotion, anniversary, achievement, milestone...'
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your client?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    }
  },
  practitionerGroupTitle: 'Personalize Motivation Session for Your Clients',
  practitionerGroupQuestions: {
    groupType: {
      label: 'What type of group are you motivating?',
      type: 'radio',
      options: ['Team/work group', 'Sports team', 'Family', 'Study group', 'Support group', 'Friends/social group']
    },
    collectiveGoal: {
      label: 'What are your clients working toward together?',
      type: 'textarea',
      placeholder: 'Describe the shared goal, project, or challenge the group is facing...'
    },
    groupDynamics: {
      label: 'What motivational approach works best for your clients?',
      type: 'radio',
      options: ['High-energy team building', 'Collaborative encouragement', 'Individual recognition within group', 'Shared accountability and support']
    },
    leadershipStyle: {
      label: 'How should the motivation be delivered to your clients?',
      type: 'radio',
      options: ['Rally-style energizing speech', 'Inclusive collaborative discussion', 'Personal reflection with group sharing', 'Action-oriented planning session']
    },
    groupChallenges: {
      label: 'What specific challenges are your clients facing as a group?',
      type: 'textarea',
      placeholder: 'Optional - describe obstacles, morale issues, or roadblocks...'
    },
    groupTriggers: {
      label: 'Why is this group\'s goal important right now?',
      type: 'textarea',
      placeholder: 'What\'s driving the group\'s need for motivation? (Optional but helps create a more impactful session)'
    },
    specialOccasions: {
      label: 'Are your clients celebrating or recognizing anything special today?',
      type: 'textarea',
      placeholder: 'e.g., team member birthday, project milestone, group achievement, anniversary...'
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your clients?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    }
  },
  groupTitle: 'Personalize Your Group Motivation Session',
  groupQuestions: {
    groupType: {
      label: 'What type of group are you motivating?',
      type: 'radio',
      options: ['Team/work group', 'Sports team', 'Family', 'Study group', 'Support group', 'Friends/social group']
    },
    collectiveGoal: {
      label: 'What is your group working toward together?',
      type: 'textarea',
      placeholder: 'Describe the shared goal, project, or challenge your group is facing...'
    },
    groupDynamics: {
      label: 'What motivational approach works best for your group?',
      type: 'radio',
      options: ['High-energy team building', 'Collaborative encouragement', 'Individual recognition within group', 'Shared accountability and support']
    },
    leadershipStyle: {
      label: 'How should the motivation be delivered to the group?',
      type: 'radio',
      options: ['Rally-style energizing speech', 'Inclusive collaborative discussion', 'Personal reflection with group sharing', 'Action-oriented planning session']
    },
    groupChallenges: {
      label: 'What specific challenges is your group facing?',
      type: 'textarea',
      placeholder: 'Optional - describe obstacles, morale issues, or roadblocks...'
    },
    groupTriggers: {
      label: 'Why is this group\'s goal important right now?',
      type: 'textarea',
      placeholder: 'What\'s driving this group\'s need for motivation? (Optional but helps create a more impactful session)'
    },
    specialOccasions: {
      label: 'Are you celebrating or recognizing anything special with your group today?',
      type: 'textarea',
      placeholder: 'e.g., team member birthday, project milestone, group achievement, anniversary...'
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your group?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    }
  },
  giftTitle: 'Personalize Motivation Session for Your Loved One',
  giftQuestions: {
    preferredStyle: {
      label: 'What type of motivation resonates with them most?',
      type: 'radio',
      options: ['Energetic and intense', 'Calm and reassuring', 'Tough love push', 'Philosophical and thoughtful']
    },
    primaryGoal: {
      label: 'What do they need motivation for?',
      type: 'radio',
      options: ['Career success', 'Personal growth', 'Fitness and health', 'Creativity', 'Life change', 'Daily productivity']
    },
    // Using specificFocus to match regular user questions - backend expects this field
    specificFocus: {
      label: 'What challenge are they facing?',
      type: 'textarea',
      placeholder: 'Describe what they need motivation for - work, personal goal, life transition...'
    },
    additionalNotes: {
      label: 'Anything else the AI should know to create a perfect session for them?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  }
};
